<script lang="ts">
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { strokeColor } from "../../lib/designTokens";
  import StrokePill from "../../lib/StrokePill.svelte";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  // Find the best and worst SWOLF values for scale
  let bestSwolf = $derived(dw.swolfStats?.bestLaps[0]?.swolf ?? null);
  let worstSwolf = $derived(dw.swolfStats?.worstLaps[0]?.swolf ?? null);

  /** True when ≥2 of the top 3 silk sets coincide with the start of a phase. */
  let silkPhenomenon = $derived((dw.swolfStats?.silkAtPhaseStart ?? 0) >= 2);

  function phaseLabelForLap(lapIndex: number): string | null {
    const phase = dw.phases.find((p) => p.lapStart === lapIndex);
    return phase?.label ?? null;
  }
</script>

<article class="proto">
  <header>
    <span class="num">06</span>
    <h2>Silk Sets ✦</h2>
    <p class="story">
      Your most efficient lengths — when you're gliding, not fighting the water.
      Measured as SWOLF (strokes + seconds). <strong>Lower is better.</strong>
    </p>
  </header>

  {#if !dw.swolfStats}
    <p class="muted">No SWOLF data in this workout.</p>
  {:else}
    <div class="hero">
      <div class="stat-block">
        <span class="k">Overall</span>
        <span class="v">{dw.swolfStats.overallAvg.toFixed(0)}</span>
      </div>
      <div class="stat-block">
        <span class="k">Best length</span>
        <span class="v best">{bestSwolf}</span>
      </div>
      <div class="stat-block">
        <span class="k">Worst length</span>
        <span class="v worst">{worstSwolf}</span>
      </div>
    </div>

    <h3>Per stroke</h3>
    <div class="per-stroke">
      {#each dw.swolfStats.perStrokeAvg as p, i (i)}
        <div class="ps-row">
          <StrokePill stroke={p.stroke} />
          <span class="ps-name">{p.stroke}</span>
          <span class="ps-count muted">{p.lapCount}×</span>
          <span class="ps-bar-wrap">
            {#if worstSwolf}
              <span class="ps-bar" style:width="{(p.avgSwolf / worstSwolf) * 100}%" style:background={strokeColor(p.stroke)}></span>
            {/if}
          </span>
          <span class="ps-value">{p.avgSwolf.toFixed(0)}</span>
        </div>
      {/each}
    </div>

    <h3 class="silk-header">✦ Your silk sets</h3>
    <div class="silk-list">
      {#each dw.swolfStats.bestLaps as lap (lap.index)}
        {@const phaseLabel = phaseLabelForLap(lap.index)}
        <div class="silk-row">
          <span class="silk-lap">L{lap.index}</span>
          <StrokePill stroke={lap.strokeStyle} size="small" />
          <span class="silk-swolf">{lap.swolf}</span>
          <span class="silk-detail muted">{Math.round(lap.durationSeconds)}s · {lap.spl} strokes</span>
          {#if phaseLabel}
            <span class="silk-phase">first of {phaseLabel}</span>
          {/if}
        </div>
      {/each}
    </div>

    {#if silkPhenomenon}
      <div class="phenomenon">
        <strong>{dw.swolfStats.silkAtPhaseStart} of 3</strong> of your silk sets were the
        <strong>first length of a phase</strong>. Fresh + pre-fatigue + perfect form = silk.
      </div>
    {/if}

    <h3 class="rough-header">⤒ Roughest lengths</h3>
    <div class="rough-list">
      {#each dw.swolfStats.worstLaps as lap (lap.index)}
        <div class="rough-row">
          <span class="bw-lap">L{lap.index}</span>
          <StrokePill stroke={lap.strokeStyle} size="small" />
          <span class="bw-swolf worst">{lap.swolf}</span>
          <span class="bw-extra muted">{Math.round(lap.durationSeconds)}s · {lap.spl} strokes</span>
        </div>
      {/each}
    </div>
  {/if}
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 12px; padding: 1rem; margin: 0.75rem 0; }
  header { margin-bottom: 0.75rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-yellow); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.1rem; font-weight: 700; margin: 0.4rem 0 0.2rem; }
  h3 { font-size: 0.85rem; font-weight: 700; margin: 1rem 0 0.4rem; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }
  .muted { color: var(--text-muted); }

  .hero { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem; }
  .stat-block { background: var(--bg-canvas); padding: 0.6rem 0.5rem; border-radius: var(--radius-button); text-align: center; }
  .stat-block .k { display: block; font-size: 0.7rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 0.2rem; }
  .stat-block .v { display: block; font-size: 1.6rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  .stat-block .v.best { color: var(--accent-primary); }
  .stat-block .v.worst { color: var(--accent-fire); }

  .per-stroke { display: flex; flex-direction: column; gap: 0.4rem; }
  /* col 1 holds the stroke pill — `auto` (not a fixed 32px) so the widest
     abbrev ("Free") renders fully instead of being clipped. */
  .ps-row { display: grid; grid-template-columns: auto 1fr auto 2fr auto; gap: 0.4rem; align-items: center; font-size: 0.8rem; }
  .ps-name { font-weight: 500; }
  .ps-count { font-size: 0.7rem; }
  .ps-bar-wrap { height: 8px; background: var(--bg-canvas); border-radius: 4px; overflow: hidden; }
  .ps-bar { display: block; height: 100%; opacity: 0.85; }
  .ps-value { font-weight: 700; font-variant-numeric: tabular-nums; min-width: 28px; text-align: right; }

  .silk-header { color: var(--accent-primary); }
  .rough-header { color: var(--accent-fire); margin-top: 1rem; }

  .silk-list { display: flex; flex-direction: column; gap: 0.35rem; }
  .silk-row {
    display: grid; grid-template-columns: auto auto auto 1fr auto;
    gap: 0.5rem; align-items: center;
    background: var(--bg-canvas);
    padding: 0.5rem 0.6rem;
    border-radius: var(--radius-button);
    border: 1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent);
    font-size: 0.82rem;
    font-variant-numeric: tabular-nums;
  }
  .silk-lap { color: var(--text-muted); font-size: 0.75rem; min-width: 28px; }
  .silk-swolf {
    font-weight: 800; font-size: 1.25rem; color: var(--accent-primary); min-width: 36px;
  }
  .silk-detail { font-size: 0.72rem; }
  .silk-phase {
    font-size: 0.7rem; color: var(--accent-fire);
    background: rgba(255,87,34,0.12);
    padding: 0.15rem 0.45rem;
    border-radius: 3px;
    font-weight: 600;
  }

  .phenomenon {
    margin-top: 0.6rem; padding: 0.6rem 0.75rem;
    background: rgba(255,87,34,0.1);
    border: 1px solid rgba(255,87,34,0.35);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font-size: 0.8rem;
    line-height: 1.45;
  }
  .phenomenon strong { color: var(--accent-fire); }

  .rough-list { display: flex; flex-direction: column; gap: 0.25rem; }
  .rough-row { display: grid; grid-template-columns: auto auto auto 1fr; gap: 0.4rem; align-items: center; font-size: 0.75rem; padding: 0.3rem 0.6rem; background: var(--bg-canvas); border-radius: 4px; font-variant-numeric: tabular-nums; }
  .bw-lap { color: var(--text-muted); font-size: 0.7rem; min-width: 24px; }
  .bw-swolf { font-weight: 800; font-size: 0.95rem; min-width: 28px; }
  .bw-swolf.worst { color: var(--accent-fire); }
  .bw-extra { font-size: 0.65rem; }
</style>
